import Keycloak from '../kc-pkg'
import axios from 'axios'
import https from 'https'

const wamToken =
    process.env.NODE_ENV !== 'test'
        ? axios.create({
            baseURL: process.env.VUE_APP_API_ENDPOINT + '/audience_admin/accounts',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'content-type': 'application/x-www-form-urlencoded; charset=utf-8' },
            withCredentials: true
        })
        : null

const httpsAgent = new https.Agent({
    rejectUnauthorized: false
})

const wamTokenOptions = {
    httpsAgent: httpsAgent,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'content-type': 'application/x-www-form-urlencoded; charset=utf-8'
    }
}

let installed = false

export default {
    install: function(Vue, params) {
        if (installed) return
        installed = true

        const defaultParams = {
            init: { onLoad: 'login-required', flow: 'hybrid' }
        }
        const options = Object.assign({}, defaultParams, params)

        const watch = new Vue({
            data() {
                return {
                    ready: false,
                    authenticated: false,
                    userName: null,
                    fullName: null,
                    token: null,
                    tokenParsed: null,
                    logoutFn: null,
                    loginFn: null,
                    login: null,
                    createLoginUrl: null,
                    createLogoutUrl: null,
                    createRegisterUrl: null,
                    register: null,
                    accountManagement: null,
                    createAccountUrl: null,
                    loadUserProfile: null,
                    loadUserInfo: null,
                    subject: null,
                    idToken: null,
                    idTokenParsed: null,
                    realmAccess: null,
                    resourceAccess: null,
                    refreshToken: null,
                    refreshTokenParsed: null,
                    timeSkew: null,
                    responseMode: null,
                    responseType: null,
                    updateTokenBeforeRoute: null,
                    updateTokenBeforeRequest: null,
                    hasRealmRole: null,
                    hasResourceRole: null
                }
            }
        })
        init(options.config, watch, options)
        Object.defineProperty(Vue.prototype, '$keycloak', {
            get() {
                return watch
            }
        })
    }
}

function init(config, watch, options) {
    const ctor = sanitizeConfig(config)
    const keycloak = new Keycloak(ctor)

    watch.$once('ready', function(cb) {
        cb && cb()
    })

    keycloak.init(options.init)
    keycloak.onReady = function(authenticated) {
        updateWatchVariables(authenticated)
        watch.ready = true
        typeof options.onReady === 'function' && watch.$emit('ready', options.onReady.bind(this, keycloak))
    }
    keycloak.onAuthSuccess = function() {
        watch.updateTokenBeforeRoute = (to, from, next) => {
            const previous_kc_token = keycloak.token
            keycloak
                .updateToken(30)
                .then(function() {
                    if (to.matched.some(record => record.meta.requiresAuth)) {
                        if (keycloak.authenticated) {
                            if (previous_kc_token !== keycloak.token) {
                                return wamToken.post('/jwt_token/json', 'kc_token=' + keycloak.token, wamTokenOptions).then(function(token) {
                                    keycloak.wam_token = token.data.jwt_token
                                    next()
                                })
                            } else {
                                next()
                            }
                        } else {
                            const loginUrl = keycloak.createLoginUrl()
                            window.location.replace(loginUrl)
                        }
                    } else {
                        if (previous_kc_token !== keycloak.token) {
                            return wamToken.post('/jwt_token/json', 'kc_token=' + keycloak.token, wamTokenOptions).then(function(token) {
                                keycloak.wam_token = token.data.jwt_token
                                next()
                            })
                        } else {
                            next()
                        }
                    }
                })
                .catch(function() {
                    next()
                })
        }
        watch.updateTokenBeforeRequest = async request => {
            const previous_kc_token = keycloak.token
            if (typeof keycloak.refreshToken !== 'undefined') {
                return new Promise(function(resolve, reject) {
                    keycloak
                        .updateToken(30)
                        .then(function() {
                            if (previous_kc_token !== keycloak.token) {
                                return wamToken.post('/jwt_token/json', 'kc_token=' + keycloak.token, wamTokenOptions).then(function(token) {
                                    keycloak.wam_token = token.data.jwt_token
                                    request.headers['X-Weborama-JWTUserAuthToken'] = keycloak.wam_token
                                    request.headers['X-Weborama-User-Email'] = keycloak.tokenParsed.portal_email ?? keycloak.tokenParsed.email
                                    resolve(request)
                                })
                            } else {
                                request.headers['X-Weborama-JWTUserAuthToken'] = keycloak.wam_token
                                request.headers['X-Weborama-User-Email'] = keycloak.tokenParsed.portal_email ?? keycloak.tokenParsed.email
                                resolve(request)
                            }
                        })
                        .catch(function() {
                            keycloak.clearToken()
                            reject(Error('Unable to update token'))
                        })
                })
            } else {
                if (keycloak.wam_token === undefined) {
                    return wamToken.post('/jwt_token/json', 'kc_token=' + keycloak.token, wamTokenOptions).then(function(token) {
                        keycloak.wam_token = token.data.jwt_token
                        request.headers['X-Weborama-JWTUserAuthToken'] = keycloak.wam_token
                        request.headers['X-Weborama-User-Email'] = keycloak.tokenParsed.portal_email ?? keycloak.tokenParsed.email
                        return request
                    })
                }
            }
        }
        watch.logoutFn = () => {
            keycloak.logout({ redirectUri: options.config.logout })
        }
    }
    keycloak.onAuthRefreshSuccess = function() {
        updateWatchVariables(true)
    }

    function updateWatchVariables(isAuthenticated = false) {
        watch.authenticated = isAuthenticated
        watch.loginFn = keycloak.login
        watch.login = keycloak.login
        watch.createLoginUrl = keycloak.createLoginUrl
        watch.createLogoutUrl = keycloak.createLogoutUrl
        watch.createRegisterUrl = keycloak.createRegisterUrl
        watch.register = keycloak.register
        if (isAuthenticated) {
            watch.accountManagement = keycloak.accountManagement
            watch.createAccountUrl = keycloak.createAccountUrl
            watch.hasRealmRole = keycloak.hasRealmRole
            watch.hasResourceRole = keycloak.hasResourceRole
            watch.loadUserProfile = keycloak.loadUserProfile
            watch.loadUserInfo = keycloak.loadUserInfo
            watch.token = keycloak.token
            watch.subject = keycloak.subject
            watch.idToken = keycloak.idToken
            watch.idTokenParsed = keycloak.idTokenParsed
            watch.realmAccess = keycloak.realmAccess
            watch.resourceAccess = keycloak.resourceAccess
            watch.refreshToken = keycloak.refreshToken
            watch.refreshTokenParsed = keycloak.refreshTokenParsed
            watch.timeSkew = keycloak.timeSkew
            watch.responseMode = keycloak.responseMode
            watch.responseType = keycloak.responseType
            watch.tokenParsed = keycloak.tokenParsed
            watch.userName = keycloak.tokenParsed['preferred_username']
            watch.fullName = keycloak.tokenParsed['name']
        }
    }
}

function sanitizeConfig(config) {
    const renameProp = (oldProp, newProp, { [oldProp]: old, ...others }) => {
        return {
            [newProp]: old,
            ...others
        }
    }
    return Object.keys(config).reduce(function(previous, key) {
        if (['authRealm', 'authUrl', 'authClientId'].includes(key)) {
            const cleaned = key.replace('auth', '')
            const newKey = cleaned.charAt(0).toLowerCase() + cleaned.slice(1)
            return renameProp(key, newKey, previous)
        }
        return previous
    }, config)
}
